import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { assets } from '../lib/assets';

export default function Hero({ onGift }) {
  return (
    <section id="top" className="relative h-[100svh] min-h-[680px] w-full overflow-hidden" data-testid="hero-section">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={assets.hero}
          alt="Orchard at sunrise"
          className="w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 hero-vignette" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className="max-w-3xl"
        >
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-brand-gold tracking-[0.25em] uppercase text-xs md:text-sm mb-6 font-medium"
            data-testid="hero-eyebrow"
          >
            Pallavit · Orchard near Nashik
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-brand-bg text-5xl sm:text-7xl lg:text-[88px] leading-[1.02] tracking-tight font-light"
            data-testid="hero-headline"
          >
            Gift a Tree.<br />
            <span className="italic font-normal text-brand-gold">Grow a Legacy.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-8 text-brand-bg/90 text-lg md:text-xl max-w-xl font-light leading-relaxed"
            data-testid="hero-subtext"
          >
            Plant a living memory for someone you love — a real fruit tree,
            rooted in our family orchard near Nashik.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={onGift}
              data-testid="hero-gift-tree-button"
              className="group rounded-full bg-brand-bg text-brand-ink px-8 py-4 text-base tracking-wide font-medium hover:bg-brand-gold hover:text-brand-ink transition-all duration-500"
            >
              Adopt a Tree
              <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
            </button>
            <a
              href="#pricing"
              data-testid="hero-explore-link"
              className="rounded-full border border-brand-bg/40 text-brand-bg px-8 py-4 text-base tracking-wide font-medium hover:bg-brand-bg/10 transition-all duration-500 text-center"
            >
              Explore Plans
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#journey"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-brand-bg/70 hover:text-brand-bg flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[11px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.a>
    </section>
  );
}
